// Fuente ÚNICA de verdad para las acciones de fila de las grillas
// (especificación SiprinIA Replica §25): una misma acción nunca puede
// renderizarse dos veces en la misma fila (§11).
//
// placement:
//   inline-start → al INICIO de la fila (operativas frecuentes: Enrutar, Recibos)
//   inline-end   → al FINAL de la fila (operativas: Hoy, GPS, Préstamos)
//   overflow     → SOLO en el menú ⋮ (secundarias/CRUD/administrativas)

export type PlacementAccion = 'inline-start' | 'inline-end' | 'overflow'

export interface AccionConfig {
  id?: string
  label: string
  icon?: string
  placement?: PlacementAccion
  destructive?: boolean
  mostrar?: (row: any) => boolean
  onClick: (row: any) => void
}

export interface AccionNormalizada extends AccionConfig {
  id: string
  placement: PlacementAccion
  insertAfter?: string
  destructive: boolean
}

export interface AccionesEntrada {
  onEditar?: (row: any) => void
  onEliminar?: (row: any) => void
  rowActions?: AccionConfig[]
}

/**
 * Normaliza onEditar/onEliminar + rowActions a una lista ÚNICA de acciones:
 * - onEditar/onEliminar se convierten en Editar/Eliminar (overflow, CRUD)
 * - las rowActions que repitan id o label de una ya existente se DESCARTAN
 * - sin placement explícito → overflow (el ⋮)
 * Regla §11: una acción = una aparición.
 */
export function normalizarAcciones(
  onEditar?: (row: any) => void,
  onEliminar?: (row: any) => void,
  rowActions: AccionConfig[] = [],
): AccionNormalizada[] {
  const resultado: AccionNormalizada[] = []
  const vistos = new Set<string>()

  const agregar = (accion: AccionConfig) => {
    const clave = accion.id || accion.label
    if (vistos.has(clave)) return
    vistos.add(clave)
    resultado.push({
      ...accion,
      id: accion.id ?? accion.label,
      placement: accion.placement ?? 'overflow',
      destructive: accion.destructive ?? false,
    })
  }

  if (onEditar) agregar({ id: 'editar', label: 'Editar', icon: 'cilPencil', onClick: onEditar })
  if (onEliminar) agregar({ id: 'eliminar', label: 'Eliminar', icon: 'cilTrash', destructive: true, onClick: onEliminar })
  for (const a of rowActions) agregar(a)

  return resultado
}

/** Acciones visibles al inicio de la fila (operativas frecuentes). */
export function accionesInlineStart(acciones: AccionNormalizada[]): AccionNormalizada[] {
  return acciones.filter((a) => a.placement === 'inline-start')
}

/** Acciones visibles al final de la fila (operativas de la ruta). */
export function accionesInlineEnd(acciones: AccionNormalizada[]): AccionNormalizada[] {
  return acciones.filter((a) => a.placement === 'inline-end')
}

/** Acciones que van SOLO en el menú ⋮ (secundarias + CRUD + destructivas). */
export function accionesOverflow(acciones: AccionNormalizada[]): AccionNormalizada[] {
  return acciones.filter((a) => a.placement === 'overflow')
}

/** El ⋮ solo muestra acciones overflow — NUNCA las ya visibles (§11). */
export function accionesParaFila(acciones: AccionNormalizada[], row: any): AccionNormalizada[] {
  return acciones.filter((a) => (a.mostrar ? a.mostrar(row) : true))
}
